import { AxiosError } from 'axios';
import { useAccountState } from 'contexts/account/account-context';
import { useGet } from 'hooks/use-get';
import React, { useContext } from 'react';
import { useParams } from 'react-router';
import { getOrderSettings } from 'views/order-feed/api/get-order-settings';
import { OrderMaintenance } from 'views/order-feed/types/order-maintenance.type';
import { OrderFeedParams } from 'views/order-feed/types/order-feed-params.type';
import { getSiteSettings } from 'views/site-settings/api/site-settings';
import { SiteSettings } from 'views/site-settings/types/site-settings-type';
import { UseQueryResult } from '@tanstack/react-query';

type Props = {
  children: React.ReactNode;
};

type State = {
  orderSettingsQuery: UseQueryResult<OrderMaintenance, AxiosError<unknown, any>>;
  siteSettingsQuery: UseQueryResult<SiteSettings, AxiosError<unknown, any>>;
};

const OrderFeedSettingsContext = React.createContext<State | undefined>(undefined);

const OrderFeedSettingsProvider: React.FC<Props> = ({ children }) => {
  const params = useParams<OrderFeedParams>();
  const { account } = useAccountState();

  const orderSettingsQuery = useGet(
    ['getOrderSettings', account.company?.marketId],
    (context) => getOrderSettings(Number(context.queryKey[1])),
    {
      enabled: account.company?.features?.hasOrderFeed && !!account.company?.marketId,
    }
  );

  const siteSettingsQuery = useGet(
    ['getSiteSettings', params.globalSiteId],
    (context) => getSiteSettings(String(context.queryKey[1])),
    {
      enabled: !!params.globalSiteId,
    }
  );

  return (
    <OrderFeedSettingsContext.Provider
      value={{
        orderSettingsQuery,
        siteSettingsQuery,
      }}>
      {children}
    </OrderFeedSettingsContext.Provider>
  );
};

function useOrderFeedSettingsState() {
  const context = useContext(OrderFeedSettingsContext);
  if (context === undefined) {
    throw new Error('useOrderFeedSettingsState must be used within an OrderFeedSettingsProvider');
  }
  return context;
}

export { OrderFeedSettingsProvider, useOrderFeedSettingsState };